/**
 * 读取 MP4（ISO BMFF）结构，只取上传前检查需要的三项：视频轨编码标识、索引（moov）是否在媒体数据（mdat）之后、时长。
 * 只按需读取文件片段（顶层盒子头 + moov 盒子），1GB 文件也只读几 MB 以内。
 * 盒子格式：4 字节大小 + 4 字节类型；大小为 1 时后接 8 字节实际大小，为 0 表示延伸到文件末尾。
 */

export interface Mp4Inspection {
    /** 第一个盒子是 ftyp */
    isMp4: boolean;
    /** 视频轨的编码标识，如 avc1（H.264）、hvc1 / hev1（HEVC）；读不到为 null */
    videoCodec: string | null;
    /** 索引在媒体数据之后（未做网页优化 / Fast Start），浏览器要先取文件尾部才能起播 */
    moovAtEnd: boolean;
    /** moov 中记录的时长（秒，未取整）；读不到为 null */
    durationSeconds: number | null;
}

interface BoxHeader {
    type: string;
    /** 盒子在所读缓冲区（或文件）中的起点 */
    start: number;
    /** 盒子内容的起点（跳过头部） */
    contentStart: number;
    end: number;
}

const MAX_TOP_LEVEL_BOXES = 64;
/** moov 超过该大小时不读取（正常 1080p 长视频的索引为几 MB） */
const MAX_MOOV_BYTES = 64 * 1024 * 1024;

const fourCc = (view: DataView, offset: number): string =>
    String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));

const readUint64 = (view: DataView, offset: number): number =>
    view.getUint32(offset) * 2 ** 32 + view.getUint32(offset + 4);

/** 在 [view] 的 [offset] 处解析盒子头；[limit] 为所属范围的终点，大小为 0 时延伸到此 */
const readBoxHeader = (view: DataView, offset: number, limit: number, base = 0): BoxHeader | null => {
    if (offset + 8 > view.byteLength) return null;
    const size32 = view.getUint32(offset);
    const type = fourCc(view, offset + 4);
    let headerSize = 8;
    let size = size32;
    if (size32 === 1) {
        if (offset + 16 > view.byteLength) return null;
        size = readUint64(view, offset + 8);
        headerSize = 16;
    } else if (size32 === 0) {
        size = limit - (base + offset);
    }
    if (size < headerSize) return null;
    return { type, start: base + offset, contentStart: base + offset + headerSize, end: base + offset + size };
};

/** 遍历 [start, end) 范围内的子盒子（[view] 为整个 moov 的缓冲区，偏移相对缓冲区） */
const childBoxes = (view: DataView, start: number, end: number): BoxHeader[] => {
    const boxes: BoxHeader[] = [];
    let offset = start;
    while (offset + 8 <= end) {
        const box = readBoxHeader(view, offset, end);
        if (!box || box.end > end) break;
        boxes.push(box);
        offset = box.end;
    }
    return boxes;
};

const findChild = (view: DataView, parent: BoxHeader, type: string): BoxHeader | null =>
    childBoxes(view, parent.contentStart, parent.end).find((box) => box.type === type) ?? null;

/** 视频轨（hdlr 类型为 vide）的第一个样本描述的编码标识 */
const videoCodecOf = (view: DataView, moov: BoxHeader): string | null => {
    for (const trak of childBoxes(view, moov.contentStart, moov.end).filter((box) => box.type === 'trak')) {
        const mdia = findChild(view, trak, 'mdia');
        const hdlr = mdia && findChild(view, mdia, 'hdlr');
        // hdlr：版本与标志 4 字节、pre_defined 4 字节，之后是 4 字节的轨道类型
        if (!mdia || !hdlr || hdlr.contentStart + 12 > hdlr.end || fourCc(view, hdlr.contentStart + 8) !== 'vide') continue;
        const minf = findChild(view, mdia, 'minf');
        const stbl = minf && findChild(view, minf, 'stbl');
        const stsd = stbl && findChild(view, stbl, 'stsd');
        // stsd：版本与标志 4 字节、条目数 4 字节，之后是第一个样本描述（4 字节大小 + 4 字节编码标识）
        if (!stsd || stsd.contentStart + 16 > stsd.end) return null;
        return fourCc(view, stsd.contentStart + 12);
    }
    return null;
};

/** mvhd：版本 0 为 4 字节时间字段，版本 1 为 8 字节 */
const durationOf = (view: DataView, moov: BoxHeader): number | null => {
    const mvhd = findChild(view, moov, 'mvhd');
    if (!mvhd) return null;
    const version = view.getUint8(mvhd.contentStart);
    const timescaleOffset = mvhd.contentStart + (version === 1 ? 20 : 12);
    if (timescaleOffset + (version === 1 ? 12 : 8) > mvhd.end) return null;
    const timescale = view.getUint32(timescaleOffset);
    const duration = version === 1 ? readUint64(view, timescaleOffset + 4) : view.getUint32(timescaleOffset + 4);
    return timescale > 0 ? duration / timescale : null;
};

/** 读取文件片段；不支持 Blob.arrayBuffer 的环境退回 FileReader */
const readSlice = (file: Blob, start: number, end: number): Promise<DataView> => {
    const part = file.slice(start, end);
    if (typeof part.arrayBuffer === 'function') return part.arrayBuffer().then((buffer) => new DataView(buffer));
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(new DataView(reader.result as ArrayBuffer));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(part);
    });
};

export async function inspectMp4(file: Blob): Promise<Mp4Inspection> {
    const result: Mp4Inspection = { isMp4: false, videoCodec: null, moovAtEnd: false, durationSeconds: null };
    let offset = 0;
    let moov: BoxHeader | null = null;
    let mdatSeen = false;
    for (let i = 0; i < MAX_TOP_LEVEL_BOXES && offset + 8 <= file.size; i++) {
        const header = readBoxHeader(await readSlice(file, offset, Math.min(offset + 16, file.size)), 0, file.size, offset);
        if (!header || header.end > file.size) break;
        if (i === 0) result.isMp4 = header.type === 'ftyp';
        if (!result.isMp4) return result;
        if (header.type === 'mdat') mdatSeen = true;
        if (header.type === 'moov') {
            moov = header;
            result.moovAtEnd = mdatSeen;
            break;
        }
        offset = header.end;
    }
    if (!moov || moov.end - moov.start > MAX_MOOV_BYTES) return result;
    const view = await readSlice(file, moov.start, moov.end);
    const local: BoxHeader = { ...moov, contentStart: moov.contentStart - moov.start, end: moov.end - moov.start, start: 0 };
    result.videoCodec = videoCodecOf(view, local);
    result.durationSeconds = durationOf(view, local);
    return result;
}

/** H.264：编码标识 avc1 或 avc3 */
export const isH264 = (codec: string | null): boolean => codec === 'avc1' || codec === 'avc3';

/** 平均码率上限 8 Mbps（技术文档 1.5 第 6 项） */
export const MAX_VIDEO_BITRATE = 8_000_000;

/** 平均码率（bit/s）= 文件大小 × 8 ÷ 时长（传未取整的时长，否则不足 0.5 秒的视频取整为 0 会跳过检查）；时长未知时为 null */
export const averageBitrate = (sizeBytes: number, durationSeconds: number | null): number | null =>
    (durationSeconds && durationSeconds > 0 ? (sizeBytes * 8) / durationSeconds : null);

/** 记录与显示用的时长：四舍五入到整秒，不足 1 秒按 1 秒；时长未知时为 undefined */
export const displayDurationSeconds = (seconds: number | null | undefined): number | undefined =>
    (seconds && seconds > 0 ? Math.max(1, Math.round(seconds)) : undefined);
